import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { createInvitation, readData } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const diaryLabels = { headache: "Cefaleia", blood_pressure: "Pressão arterial", glucose: "Glicemia" };
const statusLabels = { pending: "Convite pendente", active: "Em andamento", goal_reached: "Meta atingida", awaiting_review: "Aguardando revisão", paused: "Pausado", closed: "Encerrado", archived: "Arquivado" };
const openStatuses = new Set(["pending", "active", "goal_reached", "awaiting_review", "paused"]);

function dateLabel(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

function priority(item) {
  if (item.patient?.status === "sharing_revoked") return 0;
  if (item.status === "goal_reached" || item.status === "awaiting_review") return 0;
  if (item.status === "active" && item.return_date && new Date(item.return_date).getTime() - Date.now() < 7 * 86400000) return 1;
  if (item.status === "active" && item.goal_type === "duration" && item.target_days && Math.floor((Date.now() - new Date(item.start_date).getTime()) / 86400000) >= Number(item.target_days)) return 1;
  if (item.status === "active" && !item.entryCount) return 2;
  return 3;
}

function progressText(item) {
  const rows = item.entryRows || [];
  const completed = item.diary_type === "headache" ? new Set(rows.map((row) => new Date(row.recorded_at).toLocaleDateString("en-CA"))).size : rows.length;
  if (item.goal_type === "quantity" && item.target_count) return `${completed} de ${item.target_count} ${item.diary_type === "headache" ? "dias respondidos" : "registros"}`;
  if (item.goal_type === "duration" && item.target_days) {
    const elapsed = Math.max(0, Math.min(Number(item.target_days), Math.floor((Date.now() - new Date(item.start_date).getTime()) / 86400000) + 1));
    return `${item.diary_type === "headache" ? `${completed} dias respondidos` : `${rows.length} registros`} · período ${elapsed}/${item.target_days} dias`;
  }
  if (item.goal_type === "return_date") return `Retorno em ${dateLabel(item.return_date)}`;
  return `${rows.length} registros · acompanhamento contínuo`;
}

export default function Dashboard() {
  const { profile } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [entries, setEntries] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState(null);
  const [search, setSearch] = useState("");
  const [diaryFilter, setDiaryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const createdPatient = location.state?.createdPatient;
  const createdPatientId = location.state?.createdPatientId;

  async function load() {
    setBusy(true);
    setError("");
    try {
      const [patientResult, followupResult, entryResult] = await Promise.all([
        readData("patients", { limit: "500" }),
        readData("followups", { limit: "1000" }),
        readData("entries", { limit: "2000", sort: "recorded_at", order: "desc" }),
      ]);
      const patientRows = patientResult.data || [];
      const entryRows = entryResult.data || [];
      const patientMap = new Map(patientRows.map((row) => [String(row.id), row]));
      const entryMap = new Map();
      for (const row of entryRows) {
        const key = String(row.followup_id);
        const group = entryMap.get(key) || [];
        group.push(row);
        entryMap.set(key, group);
      }
      setPatients(patientRows);
      setEntries(entryRows);
      setItems((followupResult.data || []).map((row) => ({
        ...row,
        patient: patientMap.get(String(row.patient_id)),
        entryCount: (entryMap.get(String(row.id)) || []).length,
        entryRows: entryMap.get(String(row.id)) || [],
        lastEntry: (entryMap.get(String(row.id)) || [])[0] || null,
      })).filter((row) => row.patient));
    } catch (cause) {
      setError(cause.message || "Não foi possível carregar os acompanhamentos.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, []);

  const activePatients = useMemo(() => new Set(items.filter((item) => openStatuses.has(item.status)).map((item) => String(item.patient_id))).size, [items]);
  const reviewItems = useMemo(() => items.filter((item) => priority(item) < 3), [items]);
  const reachedItems = items.filter((item) => item.status === "goal_reached").length;
  const filtered = useMemo(() => items.filter((item) => {
    const term = search.trim().toLocaleLowerCase("pt-BR");
    const matchesName = !term || item.patient?.name?.toLocaleLowerCase("pt-BR").includes(term);
    const matchesDiary = diaryFilter === "all" || item.diary_type === diaryFilter;
    const matchesStatus = statusFilter === "all" || (statusFilter === "open" ? openStatuses.has(item.status) : item.status === statusFilter);
    return matchesName && matchesDiary && matchesStatus;
  }).sort((a, b) => priority(a) - priority(b) || String(a.patient?.name || "").localeCompare(String(b.patient?.name || ""), "pt-BR")), [items, search, diaryFilter, statusFilter]);

  async function send(patient) {
    setError("");
    try {
      const result = await createInvitation(patient.id);
      setInvite({ ...result, patient: patient.name });
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(result.link);
    } catch (cause) {
      setError(cause.message || "Não foi possível preparar o convite.");
    }
  }

  const created = patients.find((patient) => String(patient.id) === String(createdPatientId));

  return (
    <AppLayout>
      <div className="dashboard-heading">
        <div><p className="sobretitulo">Acompanhamento profissional</p><h1>Olá{profile?.name ? `, ${profile.name.split(" ")[0]}` : ""}.</h1><p>Veja quem está em acompanhamento e o que merece sua revisão.</p></div>
        <Link className="botao primario" to="/app/novo">Novo acompanhamento</Link>
      </div>
      {createdPatient && created && !invite && <aside className="created-banner"><div><strong>{createdPatient} está cadastrado.</strong><p>O diário começa quando a pessoa ativa o convite.</p></div><button className="botao primario" onClick={() => send(created)}>Preparar convite</button></aside>}
      {error && <p className="erro" role="alert">{error}</p>}
      {invite && <aside className="created-banner invite-banner"><div><strong>Convite de {invite.patient}</strong><p>{invite.email_sent ? "Enviado por e-mail." : "O e-mail não foi confirmado pelo serviço; o link foi copiado para você compartilhar."} Expira em {dateLabel(invite.expires_at)}.</p><a href={invite.link}>{invite.link}</a></div><button className="botao secundario" onClick={() => setInvite(null)}>Fechar</button></aside>}

      <section className="summary-strip" aria-label="Resumo dos acompanhamentos">
        <div><span>Pacientes ativos</span><strong>{busy ? "—" : activePatients}</strong><small>com ao menos um acompanhamento aberto</small></div>
        <div><span>Para revisar</span><strong>{busy ? "—" : reviewItems.length}</strong><small>meta atingida, retorno próximo ou sem registros</small></div>
        <div><span>Metas atingidas</span><strong>{busy ? "—" : reachedItems}</strong><small>o acompanhamento continua aberto</small></div>
        <div><span>Registros recebidos</span><strong>{busy ? "—" : entries.length}</strong><small>somente o que foi registrado</small></div>
      </section>

      <section className="queue-section">
        <div className="queue-heading"><div><h2>Acompanhamentos</h2><p>{busy ? "Carregando…" : `${filtered.length} ${filtered.length === 1 ? "resultado" : "resultados"}`}</p></div><button className="botao-link" onClick={load} disabled={busy}>Atualizar</button></div>
        <div className="queue-filters">
          <label className="search-field"><span className="sr-only">Buscar paciente</span><input type="search" placeholder="Buscar pelo nome" value={search} onChange={(event) => setSearch(event.target.value)} /></label>
          <label><span className="sr-only">Filtrar por diário</span><select value={diaryFilter} onChange={(event) => setDiaryFilter(event.target.value)}><option value="all">Todos os diários</option>{Object.entries(diaryLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <label><span className="sr-only">Filtrar por situação</span><select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="open">Em aberto</option><option value="all">Todas as situações</option>{Object.entries(statusLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
        </div>
        {busy ? <div className="loading-row" aria-live="polite">Carregando acompanhamentos…</div> : filtered.length === 0 ? <div className="empty-queue"><h3>{items.length ? "Nenhum acompanhamento com esses filtros." : "Você ainda não criou um acompanhamento."}</h3><p>{items.length ? "Tente outra busca ou situação." : "Cadastre o mínimo necessário e envie um convite ao paciente."}</p>{!items.length && <Link className="botao primario" to="/app/novo">Criar primeiro acompanhamento</Link>}</div> : (
          <div className="followup-list">
            {filtered.map((item) => {
              const latest = item.lastEntry?.recorded_at ? new Date(item.lastEntry.recorded_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) : "Ainda sem registros";
              const goal = progressText(item);
              return <article className={`followup-row priority-${priority(item)}`} key={item.id}>
                <div className="followup-person"><strong>{item.patient.name}</strong><span>{diaryLabels[item.diary_type] || "Diário"}</span></div>
                <div className="followup-goal"><span>Progresso</span><strong>{goal}</strong></div>
                <div className="followup-latest"><span>Último registro</span><strong>{latest}</strong></div>
                <span className={`status ${item.patient.status === "sharing_revoked" ? "status-paused" : `status-${item.status}`}`}>{item.patient.status === "sharing_revoked" ? "Paciente encerrou novos registros" : statusLabels[item.status] || item.status}</span>
                <div className="followup-actions"><Link className="botao secundario" to={`/app/acompanhamentos/${item.id}`}>Abrir histórico</Link>{item.status === "pending" && <button className="botao-link" onClick={() => send(item.patient)}>Reenviar convite</button>}</div>
              </article>;
            })}
          </div>
        )}
      </section>
      <p className="clinical-boundary">O DiárioMed organiza registros declarados pelo paciente. Ele não monitora urgências nem substitui a avaliação clínica.</p>
    </AppLayout>
  );
}
